import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { 
  Mail, Settings, Send, RefreshCw, CheckCircle, XCircle, 
  Clock, Eye, Search, Filter, Save, TestTube, History,
  AlertTriangle, Inbox, ArrowRight, Server, Plus, Trash2
} from 'lucide-react';

const STATUS_COLORS = {
  ENVIADO: { bg: 'bg-green-100', text: 'text-green-800', label: 'Enviado' },
  PENDIENTE: { bg: 'bg-amber-100', text: 'text-amber-800', label: 'Pendiente' },
  ERROR: { bg: 'bg-red-100', text: 'text-red-800', label: 'Error' },
  REENVIADO: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Reenviado' },
};

const NotificacionesConfig = () => {
  const [activeTab, setActiveTab] = useState('config');
  const [loading, setLoading] = useState(true);
  
  // Config state
  const [config, setConfig] = useState({
    smtp_host: '',
    smtp_port: 587,
    smtp_user: '',
    smtp_password: '',
    smtp_from_email: '',
    smtp_from_name: 'AlertGuard',
    smtp_use_tls: true,
    notificaciones_activas: true,
  });
  
  // Log state
  const [logs, setLogs] = useState([]);
  const [logFilters, setLogFilters] = useState({ status: '', search: '' });
  const [selectedLog, setSelectedLog] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showTestDialog, setShowTestDialog] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testing, setTesting] = useState(false);

  // Rotación de cuentas de envío
  const [rotacion, setRotacion] = useState(null);
  const [nuevaCuenta, setNuevaCuenta] = useState('');
  const [limiteEdit, setLimiteEdit] = useState('');
  const [rotLoading, setRotLoading] = useState(false);

  const loadRotacion = useCallback(async () => {
    try {
      const res = await api.get('/notifications/rotacion');
      setRotacion(res.data);
      setLimiteEdit(String(res.data?.limite ?? 80));
    } catch (error) {
      console.error('Error loading rotacion:', error);
    }
  }, []);

  const handleAddCuenta = async () => {
    const email = (nuevaCuenta || '').trim().toLowerCase();
    if (!email || !email.includes('@')) { toast.error('Ingresá un email válido'); return; }
    setRotLoading(true);
    try {
      const res = await api.post('/notifications/rotacion/cuentas', { email });
      setRotacion(res.data);
      setNuevaCuenta('');
      toast.success('Cuenta agregada a la rotación');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al agregar cuenta');
    } finally {
      setRotLoading(false);
    }
  };

  const handleRemoveCuenta = async (email) => {
    if (!window.confirm(`¿Quitar ${email} de la rotación?`)) return;
    try {
      const res = await api.delete('/notifications/rotacion/cuentas', { params: { email } });
      setRotacion(res.data);
      toast.success('Cuenta eliminada');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  const handleToggleCuenta = async (email, activo) => {
    try {
      const res = await api.patch('/notifications/rotacion/cuentas', { email, activo });
      setRotacion(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar cuenta');
    }
  };

  const handleSaveLimite = async () => {
    const val = parseInt(limiteEdit, 10);
    if (!val || val < 1) { toast.error('Ingresá un límite válido'); return; }
    try {
      const res = await api.put('/notifications/rotacion/limite', { limite: val });
      setRotacion(res.data);
      toast.success(`Límite actualizado a ${val} correos por cuenta`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar límite');
    }
  };

  const loadConfig = useCallback(async () => {
    try {
      const response = await api.get('/notifications/config');
      setConfig(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      console.error('Error loading config:', error);
    }
  }, []);

  const loadLogs = useCallback(async () => {
    try {
      const params = { limit: 50 };
      if (logFilters.status) params.status = logFilters.status;
      if (logFilters.search) params.search = logFilters.search;
      
      // Usar el endpoint correcto que tiene todos los logs de email
      const response = await api.get('/onboarding-config/email-logs', { params });
      const logsData = response.data?.logs || [];
      
      // Mapear los campos para compatibilidad con la UI
      const mappedLogs = logsData.map(log => ({
        id: log.id,
        tipo: log.type || 'GENERAL',
        destinatario: log.to,
        asunto: log.subject,
        status: log.status === 'SENT' ? 'ENVIADO' : (log.status === 'FAILED' ? 'ERROR' : log.status),
        fecha: log.created_at,
        intentos: 1,
        error: log.error || null
      }));
      
      setLogs(mappedLogs);
    } catch (error) {
      console.error('Error loading logs:', error);
      setLogs([]);
    }
  }, [logFilters]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([loadConfig(), loadLogs(), loadRotacion()]);
      setLoading(false);
    };
    loadData();
  }, [loadConfig, loadLogs, loadRotacion]);

  const handleSaveConfig = async () => {
    try {
      await api.put('/notifications/config', config);
      toast.success('Configuración guardada');
    } catch (error) {
      toast.error('Error al guardar configuración');
    }
  };

  const handleTestConnection = async () => {
    if (!testEmail) {
      toast.error('Ingresá un email para la prueba');
      return;
    }
    
    setTesting(true);
    try {
      await api.post('/notifications/test', { email: testEmail });
      toast.success('Email de prueba enviado');
      setShowTestDialog(false);
      setTestEmail('');
      loadLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar email de prueba');
    } finally {
      setTesting(false);
    }
  };

  const handleResend = async (logId) => {
    try {
      await api.post(`/notifications/resend/${logId}`);
      toast.success('Notificación reenviada');
      loadLogs();
    } catch (error) {
      toast.error('Error al reenviar');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('es-AR', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const config = STATUS_COLORS[status] || STATUS_COLORS.PENDIENTE;
    return (
      <Badge className={`${config.bg} ${config.text}`}>
        {config.label}
      </Badge>
    );
  };

  return (
    <div className="p-6 lg:p-8 h-full overflow-auto bg-slate-50" data-testid="notificaciones-config-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Mail size={28} className="text-blue-600" />
            Notificaciones Email
          </h1>
          <p className="text-slate-600 mt-1">Configuración de email y log de notificaciones</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => { loadConfig(); loadLogs(); }}>
            <RefreshCw size={16} className="mr-2" />
            Actualizar
          </Button>
          <Button onClick={() => setShowTestDialog(true)} style={{ backgroundColor: '#3b82f6', color: 'white' }}>
            <TestTube size={16} className="mr-2" />
            Enviar Prueba
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <Card className="p-4 bg-white">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-100">
              <Mail className="text-blue-600" size={24} />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{logs.length}</p>
              <p className="text-sm text-slate-500">Total Enviados</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 bg-white">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-green-100">
              <CheckCircle className="text-green-600" size={24} />
            </div>
            <div>
              <p className="text-2xl font-bold text-green-600">
                {logs.filter(l => l.status === 'ENVIADO').length}
              </p>
              <p className="text-sm text-slate-500">Exitosos</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 bg-white">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-red-100">
              <XCircle className="text-red-600" size={24} />
            </div>
            <div>
              <p className="text-2xl font-bold text-red-600">
                {logs.filter(l => l.status === 'ERROR').length}
              </p>
              <p className="text-sm text-slate-500">Con Error</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 bg-white">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-100">
              <Clock className="text-amber-600" size={24} />
            </div>
            <div>
              <p className="text-2xl font-bold text-amber-600">
                {logs.filter(l => l.status === 'PENDIENTE').length}
              </p>
              <p className="text-sm text-slate-500">Pendientes</p>
            </div>
          </div>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-slate-100 p-1">
          <TabsTrigger value="config" className="data-[state=active]:bg-white">
            <Settings size={16} className="mr-2" />
            Configuración SMTP
          </TabsTrigger>
          <TabsTrigger value="logs" className="data-[state=active]:bg-white">
            <History size={16} className="mr-2" />
            Log de Envíos
          </TabsTrigger>
          <TabsTrigger value="rotacion" className="data-[state=active]:bg-white" data-testid="tab-rotacion">
            <Server size={16} className="mr-2" />
            Cuentas de envío
          </TabsTrigger>
        </TabsList>

        {/* Config Tab */}
        <TabsContent value="config" className="space-y-4">
          <Card className="p-6 bg-white">
            <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Settings size={20} className="text-slate-500" />
              Configuración del Servidor de Email
            </h3>
            
            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <Label>Servidor SMTP</Label>
                  <Input
                    value={config.smtp_host}
                    onChange={(e) => setConfig({ ...config, smtp_host: e.target.value })}
                    placeholder="smtp.gmail.com"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Puerto</Label>
                  <Input
                    type="number"
                    value={config.smtp_port}
                    onChange={(e) => setConfig({ ...config, smtp_port: parseInt(e.target.value) })}
                    placeholder="587"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Usuario SMTP</Label>
                  <Input
                    value={config.smtp_user}
                    onChange={(e) => setConfig({ ...config, smtp_user: e.target.value })}
                    placeholder="tu-email@gmail.com"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Contraseña / App Password</Label>
                  <Input
                    type="password"
                    value={config.smtp_password}
                    onChange={(e) => setConfig({ ...config, smtp_password: e.target.value })}
                    placeholder="••••••••"
                    className="mt-1"
                  />
                </div>
              </div>
              
              <div className="space-y-4">
                <div>
                  <Label>Email del Remitente</Label>
                  <Input
                    value={config.smtp_from_email}
                    onChange={(e) => setConfig({ ...config, smtp_from_email: e.target.value })}
                    placeholder="alertas@tuempresa.com"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Nombre del Remitente</Label>
                  <Input
                    value={config.smtp_from_name}
                    onChange={(e) => setConfig({ ...config, smtp_from_name: e.target.value })}
                    placeholder="AlertGuard"
                    className="mt-1"
                  />
                </div>
                <div className="flex items-center gap-3 mt-6">
                  <Switch
                    checked={config.smtp_use_tls}
                    onCheckedChange={(v) => setConfig({ ...config, smtp_use_tls: v })}
                  />
                  <Label>Usar TLS/SSL</Label>
                </div>
                <div className="flex items-center gap-3">
                  <Switch
                    checked={config.notificaciones_activas}
                    onCheckedChange={(v) => setConfig({ ...config, notificaciones_activas: v })}
                  />
                  <Label>Notificaciones Activas</Label>
                </div>
              </div>
            </div>
            
            <div className="flex justify-end mt-6 pt-4 border-t">
              <Button onClick={handleSaveConfig} className="bg-slate-900 text-white hover:bg-slate-800">
                <Save size={16} className="mr-2" />
                Guardar Configuración
              </Button>
            </div>
          </Card>
          
          <Card className="p-4 bg-amber-50 border-amber-200">
            <div className="flex items-start gap-3">
              <AlertTriangle className="text-amber-600 mt-0.5" size={20} />
              <div>
                <h4 className="font-medium text-amber-800">Nota sobre Gmail</h4>
                <p className="text-sm text-amber-700 mt-1">
                  Si usás Gmail, necesitás generar una "Contraseña de Aplicación" desde la configuración de seguridad de tu cuenta Google. 
                  La contraseña normal no funcionará si tenés 2FA activado.
                </p>
              </div>
            </div>
          </Card>
        </TabsContent>

        {/* Logs Tab */}
        <TabsContent value="logs" className="space-y-4">
          {/* Filters */}
          <Card className="p-4 bg-white">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 flex-1 max-w-md">
                <Search size={18} className="text-slate-400" />
                <Input
                  placeholder="Buscar por destinatario o asunto..."
                  value={logFilters.search}
                  onChange={(e) => setLogFilters({ ...logFilters, search: e.target.value })}
                  className="flex-1"
                />
              </div>
              <Select 
                value={logFilters.status || "all"} 
                onValueChange={(v) => setLogFilters({ ...logFilters, status: v === "all" ? "" : v })}
              >
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="ENVIADO">Enviados</SelectItem>
                  <SelectItem value="ERROR">Con Error</SelectItem>
                  <SelectItem value="PENDIENTE">Pendientes</SelectItem>
                  <SelectItem value="REENVIADO">Reenviados</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </Card>

          {/* Logs Table */}
          <Card className="bg-white">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Destinatario</TableHead>
                  <TableHead>Asunto</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Intentos</TableHead>
                  <TableHead>Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      <RefreshCw className="animate-spin mx-auto text-slate-400" size={24} />
                    </TableCell>
                  </TableRow>
                ) : logs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      <Inbox className="mx-auto text-slate-300 mb-2" size={40} />
                      <p className="text-slate-500">No hay notificaciones en el log</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  logs.map(log => (
                    <TableRow key={log.id}>
                      <TableCell className="text-sm text-slate-600">{formatDate(log.fecha)}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{log.tipo}</Badge>
                      </TableCell>
                      <TableCell className="font-medium">{log.destinatario}</TableCell>
                      <TableCell className="max-w-xs truncate">{log.asunto}</TableCell>
                      <TableCell>{getStatusBadge(log.status)}</TableCell>
                      <TableCell className="text-center">{log.intentos || 1}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            onClick={() => { setSelectedLog(log); setShowDetailDialog(true); }}
                          >
                            <Eye size={14} />
                          </Button>
                          {log.status === 'ERROR' && (
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={() => handleResend(log.id)}
                              className="text-blue-600"
                            >
                              <Send size={14} />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* Rotación de cuentas de envío Tab */}
        <TabsContent value="rotacion" className="space-y-4">
          <Card className="p-4 bg-teal-50 border-teal-200" data-testid="rotacion-resumen-card">
            <div className="flex flex-wrap items-center gap-x-8 gap-y-3">
              <div>
                <p className="text-xs text-teal-700 uppercase font-medium">Cuenta que enviará ahora</p>
                <p className="text-lg font-bold text-teal-900 font-mono" data-testid="rotacion-cuenta-actual">{rotacion?.cuenta_actual || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-teal-700 uppercase font-medium">Capacidad restante hoy</p>
                <p className="text-lg font-bold text-teal-900">{rotacion?.capacidad_restante ?? 0} correo(s)</p>
              </div>
              <div>
                <p className="text-xs text-teal-700 uppercase font-medium">Cuentas activas</p>
                <p className="text-lg font-bold text-teal-900">{rotacion?.activas ?? 0}</p>
              </div>
              <Button variant="outline" size="sm" onClick={loadRotacion} className="ml-auto border-teal-300 text-teal-700">
                <RefreshCw size={14} className="mr-1.5" /> Actualizar
              </Button>
            </div>
          </Card>

          <Card className="p-6 bg-white">
            <h3 className="font-semibold text-slate-900 mb-1 flex items-center gap-2">
              <Server size={20} className="text-slate-500" /> Cuentas remitentes (rotación automática)
            </h3>
            <p className="text-sm text-slate-500 mb-4">Los correos de casos se envían rotando entre estas cuentas. Al alcanzar el límite, se pasa automáticamente a la siguiente. Los contadores se reinician cada 24hs.</p>

            {/* Límite editable */}
            <div className="flex items-end gap-3 mb-5 p-3 rounded-lg bg-slate-50 border border-slate-200">
              <div>
                <Label>Límite de correos por cuenta (antes de rotar)</Label>
                <Input type="number" min={1} value={limiteEdit} onChange={(e) => setLimiteEdit(e.target.value)}
                  data-testid="rotacion-limite-input" className="mt-1 w-40" />
              </div>
              <Button onClick={handleSaveLimite} variant="outline" data-testid="rotacion-limite-save-btn">
                <Save size={14} className="mr-1.5" /> Guardar límite
              </Button>
            </div>

            {/* Agregar cuenta */}
            <div className="flex items-end gap-2 mb-4">
              <div className="flex-1">
                <Label>Agregar cuenta de envío</Label>
                <Input value={nuevaCuenta} onChange={(e) => setNuevaCuenta(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddCuenta()}
                  placeholder="noresponder7@waya.com.ar" data-testid="rotacion-nueva-cuenta-input" className="mt-1 font-mono" />
              </div>
              <Button onClick={handleAddCuenta} disabled={rotLoading} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="rotacion-add-btn">
                <Plus size={16} className="mr-1.5" /> Agregar
              </Button>
            </div>

            {/* Lista de cuentas */}
            <Table data-testid="rotacion-tabla">
              <TableHeader>
                <TableRow>
                  <TableHead>Cuenta</TableHead>
                  <TableHead>Enviados hoy</TableHead>
                  <TableHead>Activa</TableHead>
                  <TableHead className="text-right">Acción</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(rotacion?.cuentas || []).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-8 text-slate-500">
                      No hay cuentas configuradas. Agregá una arriba.
                    </TableCell>
                  </TableRow>
                ) : (
                  (rotacion?.cuentas || []).map((c) => {
                    const pct = c.limite ? Math.min(100, Math.round((c.enviados_hoy / c.limite) * 100)) : 0;
                    const esActual = c.email === rotacion?.cuenta_actual;
                    return (
                      <TableRow key={c.email} data-testid={`rotacion-row-${c.email}`}>
                        <TableCell className="font-mono text-sm">
                          {c.email}
                          {esActual && <Badge className="ml-2 bg-teal-100 text-teal-800">Actual</Badge>}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="w-24 h-2 rounded-full bg-slate-200 overflow-hidden">
                              <div className={`h-full ${pct >= 100 ? 'bg-red-500' : pct >= 80 ? 'bg-amber-500' : 'bg-teal-500'}`} style={{ width: `${pct}%` }} />
                            </div>
                            <span className="text-xs text-slate-600">{c.enviados_hoy}/{c.limite}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Switch checked={c.activo} onCheckedChange={(v) => handleToggleCuenta(c.email, v)}
                            data-testid={`rotacion-toggle-${c.email}`} />
                        </TableCell>
                        <TableCell className="text-right">
                          <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleRemoveCuenta(c.email)} data-testid={`rotacion-remove-${c.email}`}>
                            <Trash2 size={14} />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Test Email Dialog */}
      <Dialog open={showTestDialog} onOpenChange={setShowTestDialog}>
        <DialogContent style={{ backgroundColor: '#ffffff' }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TestTube className="text-blue-600" size={20} />
              Enviar Email de Prueba
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <Label>Email de destino</Label>
            <Input
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="tu-email@ejemplo.com"
              className="mt-1"
            />
            <p className="text-sm text-slate-500 mt-2">
              Se enviará un email de prueba para verificar la configuración SMTP.
            </p>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTestDialog(false)}>Cancelar</Button>
            <Button onClick={handleTestConnection} disabled={testing || !testEmail} className="bg-slate-900 text-white hover:bg-slate-800">
              {testing ? (
                <RefreshCw className="animate-spin mr-2" size={16} />
              ) : (
                <Send size={16} className="mr-2" />
              )}
              Enviar Prueba
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Log Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent style={{ backgroundColor: '#ffffff' }}>
          {selectedLog && (
            <>
              <DialogHeader>
                <DialogTitle>Detalle de Notificación</DialogTitle>
              </DialogHeader>
              
              <div className="py-4 space-y-4">
                <div className="flex items-center gap-4">
                  {getStatusBadge(selectedLog.status)}
                  <span className="text-sm text-slate-500">{formatDate(selectedLog.fecha)}</span>
                </div>
                
                <div>
                  <Label className="text-slate-500">Tipo</Label>
                  <p className="font-medium">{selectedLog.tipo}</p>
                </div>
                
                <div>
                  <Label className="text-slate-500">Destinatario</Label>
                  <p className="font-medium">{selectedLog.destinatario}</p>
                </div>
                
                <div>
                  <Label className="text-slate-500">Asunto</Label>
                  <p className="font-medium">{selectedLog.asunto}</p>
                </div>
                
                <div>
                  <Label className="text-slate-500">Intentos de envío</Label>
                  <p className="font-medium">{selectedLog.intentos || 1}</p>
                </div>
                
                {selectedLog.error && (
                  <div className="p-3 bg-red-50 rounded-lg">
                    <Label className="text-red-700">Error</Label>
                    <p className="text-sm text-red-600 mt-1">{selectedLog.error}</p>
                  </div>
                )}
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowDetailDialog(false)}>Cerrar</Button>
                {selectedLog.status === 'ERROR' && (
                  <Button onClick={() => { handleResend(selectedLog.id); setShowDetailDialog(false); }} className="bg-slate-900 text-white hover:bg-slate-800">
                    <Send size={16} className="mr-2" />
                    Reenviar
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NotificacionesConfig;
